import { BRAND_SVG_REGISTRY } from "./brand-svg-registry";
import { DeepSeekWhaleIcon, GrokIcon } from "../icons";

type BrandIconKey = keyof typeof BRAND_SVG_REGISTRY | "deepseek" | "grok";

interface BrandIconProps {
  name?: string | null;
  brand?: string | null;
  className?: string;
  title?: string;
  fallback?: boolean;
  mono?: boolean;
}

interface BrandNameProps extends BrandIconProps {
  label: string;
  iconClassName?: string;
}

const BRAND_ALIASES: Record<string, BrandIconKey> = {
  apple: "apple",
  anthropic: "anthropic",
  browser: "playwright",
  browseroperator: "playwright",
  chrome: "google",
  closecrm: "close",
  codex: "openai",
  deepseek: "deepseek",
  deepseekdirect: "deepseek",
  deepseekv4pro: "deepseek",
  figma: "figma",
  framer: "framer",
  github: "github",
  gmail: "gmail",
  google: "google",
  grok: "grok",
  linear: "linear",
  notion: "notion",
  ocgo: "opencode",
  openai: "openai",
  openaicodex: "openai",
  opencode: "opencode",
  opencodego: "opencode",
  openrouter: "openrouter",
  playwright: "playwright",
  qc: "quantconnect",
  qcmcp: "quantconnect",
  quantconnect: "quantconnect",
  slack: "slack",
  solvyslinear: "linear",
  solvyssupport: "linear",
  solvyssupportvialinear: "linear",
  substack: "substack",
  supabase: "supabase",
  telegram: "telegram",
  tradesea: "tradesea",
  tradingview: "tradingview",
  tv: "tradingview",
  twitter: "x",
  vercel: "vercel",
  web: "playwright",
  x: "x",
  xai: "grok",
  xbookmarks: "x",
  xsearch: "x",
  yahoo: "yahoo",
  yahoofinance: "yahoo",
  youtube: "youtube",
};

const FALLBACK_LABELS: Record<string, string> = {
  arbitrum: "AR",
  arbitrumchamber: "AR",
  benzinga: "BZ",
  bloomberg: "BB",
  boardroom: "BR",
  consul: "CC",
  consulcontrol: "CC",
  financialjuice: "FJ",
  financialmodelingprep: "FM",
  fmp: "FM",
  ibkr: "IB",
  interactivebrokers: "IB",
  kalshi: "K",
  marketwatch: "MW",
  mmt: "MM",
  nous: "N",
  nousportal: "NP",
  polymarket: "PM",
  projectx: "TX",
  public: "P",
  reuters: "R",
  riskflow: "RF",
  rithmic: "R",
  topstep: "TX",
  topstepx: "TX",
  tradovate: "TV",
  tradeeconomics: "TE",
  unusualwhales: "UW",
};

export function BrandIcon({
  name,
  brand,
  className = "h-4 w-4",
  title,
  fallback = false,
  mono = false,
}: BrandIconProps) {
  const rawName = brand ?? name;
  const key = resolveBrandIconKey(rawName);
  const accessible = title || undefined;

  if (key === "grok") {
    return <GrokIcon className={className} />;
  }
  if (key === "deepseek") {
    return <DeepSeekWhaleIcon className={className} />;
  }

  const data =
    key && key in BRAND_SVG_REGISTRY ? BRAND_SVG_REGISTRY[key] : null;
  if (data) {
    return (
      <svg
        viewBox={data.viewBox ?? "0 0 24 24"}
        className={className}
        role={accessible ? "img" : undefined}
        aria-hidden={accessible ? undefined : true}
        aria-label={accessible}
      >
        {accessible ? <title>{accessible}</title> : null}
        {data.paths.map((path, index) => (
          <path
            key={`${data.title}-${index}`}
            d={path.d}
            fill={mono ? "currentColor" : (path.fill ?? "currentColor")}
          />
        ))}
      </svg>
    );
  }

  if (!fallback) return null;
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-[4px] border border-current/25 font-mono text-[0.48em] font-semibold leading-none ${className}`}
      role={accessible ? "img" : undefined}
      aria-hidden={accessible ? undefined : true}
      aria-label={accessible}
      title={accessible}
    >
      {fallbackBrandLabel(rawName)}
    </span>
  );
}

export function BrandName({
  label,
  iconClassName = "h-3.5 w-3.5",
  className = "",
  ...props
}: BrandNameProps) {
  return (
    <span className={`inline-flex min-w-0 items-center gap-1.5 ${className}`}>
      <BrandIcon
        {...props}
        name={props.name ?? label}
        className={`${iconClassName} shrink-0`}
        fallback
      />
      <span className="min-w-0 truncate">{label}</span>
    </span>
  );
}

export function resolveBrandIconKey(
  value: string | null | undefined,
): BrandIconKey | null {
  const normalized = normalizeBrandName(value);
  if (!normalized) return null;
  if (normalized in BRAND_SVG_REGISTRY) {
    return normalized as keyof typeof BRAND_SVG_REGISTRY;
  }
  return BRAND_ALIASES[normalized] ?? null;
}

export function fallbackBrandLabel(value: string | null | undefined): string {
  const normalized = normalizeBrandName(value);
  if (!normalized) return "?";
  if (FALLBACK_LABELS[normalized]) return FALLBACK_LABELS[normalized];
  const words = String(value ?? "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  if (words.length > 1)
    return words
      .slice(0, 2)
      .map((word) => word[0])
      .join("");
  return normalized.slice(0, 2).toUpperCase();
}

function normalizeBrandName(value: string | null | undefined): string {
  return String(value ?? "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "");
}
